/**
 * Bridge Tab — Admin Client Detail
 * Shows BridgeRegistration health and editable client-level bridge configuration.
 *
 * The configuration schema is strict and matches the backend-bridge-config-schema.md.
 * Only known keys are allowed; each section has typed fields.
 * Sections are optional — they can be added or removed per client needs.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle, Copy01, Plus, Trash01 } from "@untitledui/icons";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { Toggle } from "@/components/base/toggle/toggle";
import { useAdminApi } from "@/services/api/admin-api";
import type { BridgeRegistrationAdminListItemDto, DocumentTypeAdminListItemDto, OrganizationAdminListItemDto } from "@/types/api/admin";
import { isValidGuid } from "@/utils/guid";

// =============================================================================
// TYPES
// =============================================================================

interface BridgeTabProps {
    clientId: number;
}

// =============================================================================
// BRIDGE CONFIG SCHEMA — matches backend-bridge-config-schema.md exactly
// =============================================================================

/** All known feature flag keys */
const FEATURE_FLAG_KEYS = ["VendorLookup", "Relating", "Exporting", "RoutingCodeSync"] as const;
type FeatureFlagKey = (typeof FEATURE_FLAG_KEYS)[number];

/** All known connection string keys */
const CONNECTION_STRING_KEYS = ["vendorMaster", "autoSys", "targetSystem"] as const;
type ConnectionStringKey = (typeof CONNECTION_STRING_KEYS)[number];

/** Blob storage provider options */
const BLOB_PROVIDERS = ["Azure", "Vercel"] as const;

/**
 * A single row in the Target System API domain ID map.
 * Maps an organization code → its EZCM domain ID and per-org AP divisions flag.
 * The form holds the map as ordered rows for editing; it serializes to a `domainIDMap` object
 * (`orgCode → { domainId, apDivisionsEnabled }`) on save.
 */
interface DomainIdMapRow {
    orgCode: string;
    domainId: string;
    apDivisionsEnabled: boolean;
}

/**
 * EZCM convertPdf options. Labels are admin-facing; ids are the EZCM REST API wire values
 * (must match EZRestConstraint in the bridge: doc2pdf, ocr2pdf, none).
 */
const CONVERT_PDF_OPTIONS = [
    { id: "none", label: "None" },
    { id: "doc2pdf", label: "Document to PDF" },
    { id: "ocr2pdf", label: "OCR Image to PDF" },
] as const;

/**
 * A single row in the EZCM export document settings table.
 * Maps an (organization code, document type code) pair → EZCM target folder + convertPdf mode.
 * The form holds the map as ordered rows for editing; it serializes to
 * `targetSystemAPISettings.exportDocSettings` (orgCode → docTypeCode → { targetFolder, convertPdf }) on save.
 */
interface ExportDocSettingRow {
    orgCode: string;
    docTypeCode: string;
    targetFolder: string;
    convertPdf: string;
}

/**
 * A single row in the custom per-organization vendor lookup map (vendor lookup target = "custom").
 * Maps an organization code → its bespoke vendor table: connection string, table name, id/name columns,
 * and an optional phone column. The form holds the map as ordered rows for editing; it serializes to a
 * top-level `customVendorLookup` object (orgCode → { ... }) on save, only when the vendor lookup target
 * is "custom".
 */
interface CustomVendorMapRow {
    orgCode: string;
    connectionString: string;
    tableName: string;
    vendorIdColumn: string;
    vendorNameColumn: string;
    /** Optional — enables digit-normalized phone fallback search when set. */
    vendorPhoneColumn: string;
}

/**
 * Optional section keys — these are the sections that can be added/removed.
 * Core fields (identity, polling, feature flags, vendor/export) are always present.
 */
const OPTIONAL_SECTIONS = [
    { key: "connectionStrings", label: "Connection Strings", description: "SQL Server connection strings for target ERP databases" },
    { key: "vendorMasterAPISettings", label: "Vendor Master API", description: "Vendor master API settings (e.g. Sage 100 BOI Wrapper)" },
    { key: "targetSystemAPISettings", label: "Target System API", description: "Target system REST API settings (e.g. EZCM)" },
    {
        key: "exportDocSettings",
        label: "EZCM Export Document Settings",
        description: "Per-organization, per-document-type target folder and PDF conversion (EZCM API export only)",
    },
    { key: "blobStorage", label: "Blob Storage", description: "Azure Blob or Vercel Blob configuration" },
    { key: "settings", label: "Task Configuration", description: "Custom field codes for vendor and receipt lookup tasks" },
] as const;
type OptionalSectionKey = (typeof OPTIONAL_SECTIONS)[number]["key"];

/**
 * Typed bridge configuration structure.
 * Values are `string` in the form state (inputs always produce strings),
 * but are serialized to their correct types (number, boolean, object) on save.
 */
interface BridgeConfig {
    // Bridge identity (always present)
    name: string;
    isEnabled: boolean;

    // Polling configuration (always present)
    pollIntervalMs: string;
    maxConcurrentJobs: string;
    maxJobsPerCycle: string;
    configRefreshIntervalMs: string;

    // Vendor/export settings (always present)
    vendorLookup: { target: string; type: string };
    exportTarget: { target: string; type: string };
    vendorsToReturn: string;
    exportedAsCustomSeparator: string;

    // Telemetry (always present, value is optional)
    applicationInsightsConnectionString: string;

    // Feature flags (always present)
    featureFlags: Record<FeatureFlagKey, boolean>;

    // --- Optional sections below ---

    // Connection strings
    connectionStrings: Record<ConnectionStringKey, string>;

    /**
     * Per-organization vendor master map for the "custom" vendor lookup target. Edited as ordered
     * rows; serialized to a top-level `customVendorLookup` object on save (only when target=custom).
     * Rendered inside the Connection Strings section in place of the single vendorMaster input.
     */
    customVendorLookup: CustomVendorMapRow[];

    // BOI Wrapper API (Sage 100 / Crest)
    vendorMasterAPISettings: {
        baseUrl: string;
        versionPath: string;
        vendorServiceName: string;
        authKey: string;
        authSecret: string;
        module: string;
    };

    // Target System API (e.g. EZCM)
    targetSystemAPISettings: {
        baseUrl: string;
        /** Per-organization EZCM domain map, edited as ordered rows; serialized to `domainIDMap` (orgCode → { domainId, apDivisionsEnabled }). */
        domainIDMap: DomainIdMapRow[];
        /** Per-org, per-doc-type export settings, edited as ordered rows; serialized to `exportDocSettings` (orgCode → docTypeCode → { targetFolder, convertPdf }). EZCM API export only. */
        exportDocSettings: ExportDocSettingRow[];
        clientID: string;
        clientSecret: string;
        userName: string;
        password: string;
        timeoutSeconds: string;
    };

    // Blob storage
    blobStorage: {
        provider: string;
        url: string;
        connectionString: string;
        accessToken: string;
        container: string;
    };

    // Settings (task config + export/parse settings)
    settings: {
        exportsOrganizationCode: boolean;
        organizationCodeExportFieldName: string;
        organizationCodeParseFieldName: string;
        organizationCodeParseFieldNameDescription: string;
        taskConfig: {
            vendorLookup: {
                vendorNameField: string;
                nameCandidatesField: string;
                vendorIdField: string;
                vendorPhoneField: string;
                /** B1: per-client fuzzy threshold. Stored as string in form state. Empty = use bridge default. */
                minCompositeScore: string;
                /** B1: GP-only. Also query VNDCHKNM. */
                searchCheckName: boolean;
                /** B1: GP-only. Also query VENDSHNM. */
                searchShortName: boolean;
                /** B2: manual alias pairs. Edited as JSON text in the structured editor. Empty = no aliases. */
                vendorAliasesJson: string;
            };
            receiptLookup: {
                vendorIdField: string;
                invoiceDateField: string;
            };
        };
    };
}

// =============================================================================
// DEFAULT VALUES FOR EACH SECTION
// =============================================================================

const DEFAULT_CONNECTION_STRINGS: BridgeConfig["connectionStrings"] = {
    vendorMaster: "",
    autoSys: "",
    targetSystem: "",
};

const DEFAULT_VENDOR_MASTER_API: BridgeConfig["vendorMasterAPISettings"] = {
    baseUrl: "",
    versionPath: "/tables/",
    vendorServiceName: "ap_vendor_svc",
    authKey: "",
    authSecret: "",
    module: "A/P",
};

const DEFAULT_TARGET_SYSTEM_API: BridgeConfig["targetSystemAPISettings"] = {
    baseUrl: "",
    domainIDMap: [],
    exportDocSettings: [],
    clientID: "",
    clientSecret: "",
    userName: "",
    password: "",
    timeoutSeconds: "600",
};

const DEFAULT_BLOB: BridgeConfig["blobStorage"] = {
    provider: "Azure",
    url: "",
    connectionString: "",
    accessToken: "",
    container: "invoices",
};

const DEFAULT_SETTINGS: BridgeConfig["settings"] = {
    exportsOrganizationCode: false,
    organizationCodeExportFieldName: "",
    organizationCodeParseFieldName: "",
    organizationCodeParseFieldNameDescription: "",
    taskConfig: {
        vendorLookup: {
            vendorNameField: "vendor_name",
            nameCandidatesField: "name_candidates",
            vendorIdField: "vendor_id",
            vendorPhoneField: "vendor_phone",
            minCompositeScore: "",
            searchCheckName: true,
            searchShortName: true,
            vendorAliasesJson: "",
        },
        receiptLookup: {
            vendorIdField: "vendor_id",
            invoiceDateField: "invoice_date",
        },
    },
};

/** Build an empty config with defaults */
function emptyConfig(): BridgeConfig {
    return {
        name: "",
        isEnabled: true,
        pollIntervalMs: "15000",
        maxConcurrentJobs: "5",
        maxJobsPerCycle: "10",
        configRefreshIntervalMs: "300000",
        vendorLookup: { target: "", type: "" },
        exportTarget: { target: "", type: "" },
        vendorsToReturn: "2",
        exportedAsCustomSeparator: "|",
        applicationInsightsConnectionString: "",
        featureFlags: {
            VendorLookup: false,
            Relating: false,
            Exporting: false,
            RoutingCodeSync: false,
        },
        connectionStrings: { ...DEFAULT_CONNECTION_STRINGS },
        customVendorLookup: [],
        vendorMasterAPISettings: { ...DEFAULT_VENDOR_MASTER_API },
        targetSystemAPISettings: { ...DEFAULT_TARGET_SYSTEM_API },
        blobStorage: { ...DEFAULT_BLOB },
        settings: structuredClone(DEFAULT_SETTINGS),
    };
}

/** Detect which optional sections are present in the raw JSON */
function detectEnabledSections(json: string | null): Set<OptionalSectionKey> {
    const sections = new Set<OptionalSectionKey>();
    if (!json || !json.trim()) return sections;
    try {
        const raw = typeof json === "string" ? JSON.parse(json) : json;
        if (typeof raw !== "object" || raw === null) return sections;
        for (const { key } of OPTIONAL_SECTIONS) {
            if (raw[key] != null && typeof raw[key] === "object") {
                sections.add(key);
            }
        }
        // exportDocSettings lives nested inside targetSystemAPISettings on the wire,
        // so the top-level check above never finds it.
        const tsa = raw.targetSystemAPISettings;
        if (tsa != null && typeof tsa === "object" && tsa.exportDocSettings != null && typeof tsa.exportDocSettings === "object") {
            sections.add("exportDocSettings");
        }
    } catch {
        // ignore
    }
    return sections;
}

/** Parse raw JSON into typed BridgeConfig, filling missing keys with defaults */
function parseRawConfig(json: string | null): BridgeConfig {
    const defaults = emptyConfig();
    if (!json || !json.trim()) return defaults;
    try {
        const raw = typeof json === "string" ? JSON.parse(json) : json;
        if (typeof raw !== "object" || raw === null) return defaults;

        const str = (v: unknown, fallback: string) => (v != null ? String(v) : fallback);
        const bool = (v: unknown, fallback: boolean) => (typeof v === "boolean" ? v : fallback);
        const obj = (v: unknown) => (typeof v === "object" && v !== null && !Array.isArray(v) ? v : {}) as Record<string, unknown>;

        return {
            name: str(raw.name, defaults.name),
            isEnabled: bool(raw.isEnabled, defaults.isEnabled),
            pollIntervalMs: str(raw.pollIntervalMs, defaults.pollIntervalMs),
            maxConcurrentJobs: str(raw.maxConcurrentJobs, defaults.maxConcurrentJobs),
            maxJobsPerCycle: str(raw.maxJobsPerCycle, defaults.maxJobsPerCycle),
            configRefreshIntervalMs: str(raw.configRefreshIntervalMs, defaults.configRefreshIntervalMs),
            vendorLookup: (() => {
                const v = raw.vendorLookup;
                if (typeof v === "object" && v !== null) {
                    return { target: str((v as Record<string, unknown>).target, ""), type: str((v as Record<string, unknown>).type, "") };
                }
                // Legacy: raw string → treat as target with empty type
                return { target: typeof v === "string" ? v : "", type: "" };
            })(),
            exportTarget: (() => {
                const v = raw.exportTarget;
                if (typeof v === "object" && v !== null) {
                    return { target: str((v as Record<string, unknown>).target, ""), type: str((v as Record<string, unknown>).type, "") };
                }
                return { target: typeof v === "string" ? v : "", type: "" };
            })(),
            vendorsToReturn: str(raw.vendorsToReturn, defaults.vendorsToReturn),
            exportedAsCustomSeparator: str(raw.exportedAsCustomSeparator, defaults.exportedAsCustomSeparator),
            applicationInsightsConnectionString: str(raw.applicationInsightsConnectionString, defaults.applicationInsightsConnectionString),
            featureFlags: {
                VendorLookup: bool(obj(raw.featureFlags).VendorLookup, defaults.featureFlags.VendorLookup),
                Relating: bool(obj(raw.featureFlags).Relating, defaults.featureFlags.Relating),
                Exporting: bool(obj(raw.featureFlags).Exporting, defaults.featureFlags.Exporting),
                RoutingCodeSync: bool(obj(raw.featureFlags).RoutingCodeSync, defaults.featureFlags.RoutingCodeSync),
            },
            connectionStrings: {
                vendorMaster: str(obj(raw.connectionStrings).vendorMaster, defaults.connectionStrings.vendorMaster),
                autoSys: str(obj(raw.connectionStrings).autoSys, defaults.connectionStrings.autoSys),
                targetSystem: str(obj(raw.connectionStrings).targetSystem, defaults.connectionStrings.targetSystem),
            },
            // customVendorLookup: top-level map (orgCode → { connectionString, tableName, columns... }) → ordered rows.
            customVendorLookup: Object.entries(obj(raw.customVendorLookup)).map(([orgCode, value]) => {
                const v = obj(value);
                return {
                    orgCode,
                    connectionString: str(v.connectionString, ""),
                    tableName: str(v.tableName, ""),
                    vendorIdColumn: str(v.vendorIdColumn, ""),
                    vendorNameColumn: str(v.vendorNameColumn, ""),
                    vendorPhoneColumn: str(v.vendorPhoneColumn, ""),
                };
            }),
            vendorMasterAPISettings: (() => {
                const s = obj(raw.vendorMasterAPISettings);
                return {
                    baseUrl: str(s.baseUrl, defaults.vendorMasterAPISettings.baseUrl),
                    versionPath: str(s.versionPath, defaults.vendorMasterAPISettings.versionPath),
                    vendorServiceName: str(s.vendorServiceName, defaults.vendorMasterAPISettings.vendorServiceName),
                    authKey: str(s.authKey, defaults.vendorMasterAPISettings.authKey),
                    authSecret: str(s.authSecret, defaults.vendorMasterAPISettings.authSecret),
                    module: str(s.module, defaults.vendorMasterAPISettings.module),
                };
            })(),
            targetSystemAPISettings: (() => {
                const s = obj(raw.targetSystemAPISettings);
                // Legacy global flag — used as the per-row default when loading the old string-valued shape
                // so an existing config (e.g. Crest with apDivisionsEnabled: true) keeps its behavior on re-save.
                const legacyGlobal = bool(s.apDivisionsEnabled, false);
                // domainIDMap entries are either the current object shape ({ domainId, apDivisionsEnabled })
                // or the legacy bare-string shape (the domain ID only). Convert both to ordered rows for editing.
                let domainIDMap: DomainIdMapRow[] = Object.entries(obj(s.domainIDMap)).map(([orgCode, value]) => {
                    if (value != null && typeof value === "object") {
                        const v = value as Record<string, unknown>;
                        return {
                            orgCode,
                            domainId: v.domainId != null ? String(v.domainId) : "",
                            apDivisionsEnabled: bool(v.apDivisionsEnabled, false),
                        };
                    }
                    // Legacy: value was a bare domain ID string.
                    return {
                        orgCode,
                        domainId: value != null ? String(value) : "",
                        apDivisionsEnabled: legacyGlobal,
                    };
                });
                // Backward-compat: a legacy single `domainID` string with no map → keep it as one unassigned row
                // so the value isn't silently lost; the admin can then assign an organization.
                if (domainIDMap.length === 0 && typeof s.domainID === "string" && s.domainID.trim() !== "") {
                    domainIDMap = [{ orgCode: "", domainId: s.domainID, apDivisionsEnabled: legacyGlobal }];
                }
                // exportDocSettings: nested map (orgCode → docTypeCode → { targetFolder, convertPdf }) → ordered rows.
                const exportDocSettings: ExportDocSettingRow[] = Object.entries(obj(s.exportDocSettings)).flatMap(([orgCode, byDocType]) =>
                    Object.entries(obj(byDocType)).map(([docTypeCode, value]) => {
                        const v = obj(value);
                        return {
                            orgCode,
                            docTypeCode,
                            targetFolder: str(v.targetFolder, ""),
                            convertPdf: str(v.convertPdf, "none"),
                        };
                    }),
                );
                return {
                    baseUrl: str(s.baseUrl, defaults.targetSystemAPISettings.baseUrl),
                    domainIDMap,
                    exportDocSettings,
                    clientID: str(s.clientID, defaults.targetSystemAPISettings.clientID),
                    clientSecret: str(s.clientSecret, defaults.targetSystemAPISettings.clientSecret),
                    userName: str(s.userName, defaults.targetSystemAPISettings.userName),
                    password: str(s.password, defaults.targetSystemAPISettings.password),
                    timeoutSeconds: str(s.timeoutSeconds, defaults.targetSystemAPISettings.timeoutSeconds),
                };
            })(),
            blobStorage: (() => {
                const s = obj(raw.blobStorage);
                return {
                    provider: str(s.provider, defaults.blobStorage.provider),
                    url: str(s.url, defaults.blobStorage.url),
                    connectionString: str(s.connectionString, defaults.blobStorage.connectionString),
                    accessToken: str(s.accessToken, defaults.blobStorage.accessToken),
                    container: str(s.container, defaults.blobStorage.container),
                };
            })(),
            settings: (() => {
                const s = obj(raw.settings);
                const tc = obj((s as Record<string, unknown>).taskConfig);
                const vl = obj((tc as Record<string, unknown>).vendorLookup);
                const rl = obj((tc as Record<string, unknown>).receiptLookup);
                return {
                    exportsOrganizationCode: bool((s as Record<string, unknown>).exportsOrganizationCode, defaults.settings.exportsOrganizationCode),
                    organizationCodeExportFieldName: str(
                        (s as Record<string, unknown>).organizationCodeExportFieldName,
                        defaults.settings.organizationCodeExportFieldName,
                    ),
                    organizationCodeParseFieldName: str(
                        (s as Record<string, unknown>).organizationCodeParseFieldName,
                        defaults.settings.organizationCodeParseFieldName,
                    ),
                    organizationCodeParseFieldNameDescription: str(
                        (s as Record<string, unknown>).organizationCodeParseFieldNameDescription,
                        defaults.settings.organizationCodeParseFieldNameDescription,
                    ),
                    taskConfig: {
                        vendorLookup: {
                            vendorNameField: str(vl.vendorNameField, defaults.settings.taskConfig.vendorLookup.vendorNameField),
                            nameCandidatesField: str(vl.nameCandidatesField, defaults.settings.taskConfig.vendorLookup.nameCandidatesField),
                            vendorIdField: str(vl.vendorIdField, defaults.settings.taskConfig.vendorLookup.vendorIdField),
                            vendorPhoneField: str(vl.vendorPhoneField, defaults.settings.taskConfig.vendorLookup.vendorPhoneField),
                            // B1: minCompositeScore round-trips as a string in form state. Empty = unset → bridge default applies.
                            minCompositeScore: typeof vl.minCompositeScore === "number" ? String(vl.minCompositeScore) : "",
                            searchCheckName: bool(vl.searchCheckName, defaults.settings.taskConfig.vendorLookup.searchCheckName),
                            searchShortName: bool(vl.searchShortName, defaults.settings.taskConfig.vendorLookup.searchShortName),
                            // B2: aliases edit as JSON text. Empty = no aliases.
                            vendorAliasesJson: Array.isArray(vl.vendorAliases) ? JSON.stringify(vl.vendorAliases, null, 2) : "",
                        },
                        receiptLookup: {
                            vendorIdField: str(rl.vendorIdField, defaults.settings.taskConfig.receiptLookup.vendorIdField),
                            invoiceDateField: str(rl.invoiceDateField, defaults.settings.taskConfig.receiptLookup.invoiceDateField),
                        },
                    },
                };
            })(),
        };
    } catch {
        return defaults;
    }
}

/** Serialize typed BridgeConfig back to JSON, only including enabled sections */
/**
 * Serializes the vendorLookup form state to the on-wire shape. Optional B1/B2 fields
 * are emitted only when meaningfully set (non-default), so existing clients without
 * these fields stay in their current shape until an admin actually configures them.
 */
function serializeVendorLookup(vl: BridgeConfig["settings"]["taskConfig"]["vendorLookup"]): Record<string, unknown> {
    const out: Record<string, unknown> = {
        vendorNameField: vl.vendorNameField || "vendor_name",
        nameCandidatesField: vl.nameCandidatesField || "name_candidates",
        vendorIdField: vl.vendorIdField || "vendor_id",
        vendorPhoneField: vl.vendorPhoneField || "vendor_phone",
    };

    // B1: emit minCompositeScore only if the user supplied a numeric value.
    const minScore = vl.minCompositeScore.trim();
    if (minScore !== "") {
        const parsed = Number(minScore);
        if (!Number.isNaN(parsed)) {
            out.minCompositeScore = parsed;
        }
    }

    // B1: emit GP toggles only when explicitly false (defaults are true; omit-on-default keeps the wire shape stable).
    if (vl.searchCheckName === false) {
        out.searchCheckName = false;
    }
    if (vl.searchShortName === false) {
        out.searchShortName = false;
    }

    // B2: parse the alias-list JSON textbox. If it doesn't parse as an array, drop it silently — the
    // structured editor's hint should warn the user; raw-JSON mode is the escape hatch for edge cases.
    const aliasesText = vl.vendorAliasesJson.trim();
    if (aliasesText !== "") {
        try {
            const parsed = JSON.parse(aliasesText);
            if (Array.isArray(parsed)) {
                out.vendorAliases = parsed;
            }
        } catch {
            // Ignore — invalid JSON should surface as a save-time validation later if needed.
        }
    }

    return out;
}

function serializeConfig(config: BridgeConfig, enabledSections: Set<OptionalSectionKey>): Record<string, unknown> {
    const num = (v: string) => {
        const n = Number(v);
        return isNaN(n) ? 0 : n;
    };
    const strOrNull = (v: string) => (v.trim() === "" ? null : v.trim());

    const result: Record<string, unknown> = {
        name: strOrNull(config.name),
        isEnabled: config.isEnabled,
        pollIntervalMs: num(config.pollIntervalMs),
        maxConcurrentJobs: num(config.maxConcurrentJobs),
        maxJobsPerCycle: num(config.maxJobsPerCycle),
        configRefreshIntervalMs: num(config.configRefreshIntervalMs),
        vendorLookup: config.vendorLookup.target ? { target: config.vendorLookup.target, type: config.vendorLookup.type || null } : null,
        exportTarget: config.exportTarget.target ? { target: config.exportTarget.target, type: config.exportTarget.type || null } : null,
        vendorsToReturn: num(config.vendorsToReturn),
        exportedAsCustomSeparator: strOrNull(config.exportedAsCustomSeparator),
        applicationInsightsConnectionString: strOrNull(config.applicationInsightsConnectionString),
        featureFlags: { ...config.featureFlags },
    };

    if (enabledSections.has("connectionStrings")) {
        result.connectionStrings = {
            vendorMaster: strOrNull(config.connectionStrings.vendorMaster),
            autoSys: strOrNull(config.connectionStrings.autoSys),
            targetSystem: strOrNull(config.connectionStrings.targetSystem),
        };

        // customVendorLookup applies only to the "custom" vendor lookup target. When the target is
        // anything else, the section is dropped on the next save (same force-off pattern as
        // apDivisionsEnabled/exportDocSettings). Rows → object (orgCode → { ... }); rows without an
        // org code are dropped; on duplicate keys the last row wins. The optional phone field is
        // emitted only when non-empty so the wire shape stays minimal.
        if (config.vendorLookup.target === "custom") {
            const customVendorLookup: Record<string, Record<string, string>> = {};
            for (const row of config.customVendorLookup) {
                const orgCode = row.orgCode.trim();
                if (orgCode === "") continue;
                const entry: Record<string, string> = {
                    connectionString: row.connectionString.trim(),
                    tableName: row.tableName.trim(),
                    vendorIdColumn: row.vendorIdColumn.trim(),
                    vendorNameColumn: row.vendorNameColumn.trim(),
                };
                if (row.vendorPhoneColumn.trim() !== "") entry.vendorPhoneColumn = row.vendorPhoneColumn.trim();
                customVendorLookup[orgCode] = entry;
            }
            result.customVendorLookup = customVendorLookup;
        }
    }

    if (enabledSections.has("vendorMasterAPISettings")) {
        result.vendorMasterAPISettings = {
            baseUrl: strOrNull(config.vendorMasterAPISettings.baseUrl),
            versionPath: strOrNull(config.vendorMasterAPISettings.versionPath),
            vendorServiceName: strOrNull(config.vendorMasterAPISettings.vendorServiceName),
            authKey: strOrNull(config.vendorMasterAPISettings.authKey),
            authSecret: strOrNull(config.vendorMasterAPISettings.authSecret),
            module: strOrNull(config.vendorMasterAPISettings.module),
        };
    }

    if (enabledSections.has("targetSystemAPISettings")) {
        // Rows → object (orgCode → { domainId, apDivisionsEnabled }). Drop rows without an org code;
        // on duplicate keys, the last row wins.
        // AP divisions are a Sage 100-only concept (only the Sage 100 vendor-lookup connector can
        // resolve a division). For any other vendor-lookup target, force the flag off so a stale
        // value never persists and the dead config is stripped on the next save.
        const isSage100VendorLookup = config.vendorLookup.target === "sage100";
        const domainIDMap: Record<string, { domainId: string; apDivisionsEnabled: boolean }> = {};
        for (const row of config.targetSystemAPISettings.domainIDMap) {
            const orgCode = row.orgCode.trim();
            if (orgCode === "") continue;
            domainIDMap[orgCode] = {
                domainId: row.domainId.trim(),
                apDivisionsEnabled: isSage100VendorLookup ? row.apDivisionsEnabled : false,
            };
        }
        result.targetSystemAPISettings = {
            baseUrl: strOrNull(config.targetSystemAPISettings.baseUrl),
            domainIDMap,
            clientID: strOrNull(config.targetSystemAPISettings.clientID),
            clientSecret: strOrNull(config.targetSystemAPISettings.clientSecret),
            userName: strOrNull(config.targetSystemAPISettings.userName),
            password: strOrNull(config.targetSystemAPISettings.password),
            timeoutSeconds: num(config.targetSystemAPISettings.timeoutSeconds),
        };

        // exportDocSettings only applies to EZCM API exports. When the export target moves away
        // from EZCM/API, the property is silently dropped on the next save (same pattern as the
        // apDivisionsEnabled force-off above). Doc type codes are stored lowercase; the bridge
        // matches them case-insensitively. Rows missing an org or doc type are dropped; on
        // duplicate (org, docType) pairs the last row wins.
        const isEzcmApiExport = config.exportTarget.target === "ezcm" && config.exportTarget.type === "api";
        if (enabledSections.has("exportDocSettings") && isEzcmApiExport) {
            const exportDocSettings: Record<string, Record<string, { targetFolder: string; convertPdf: string }>> = {};
            for (const row of config.targetSystemAPISettings.exportDocSettings) {
                const orgCode = row.orgCode.trim();
                const docTypeCode = row.docTypeCode.trim().toLowerCase();
                if (orgCode === "" || docTypeCode === "") continue;
                (exportDocSettings[orgCode] ??= {})[docTypeCode] = {
                    targetFolder: row.targetFolder.trim(),
                    convertPdf: row.convertPdf || "none",
                };
            }
            (result.targetSystemAPISettings as Record<string, unknown>).exportDocSettings = exportDocSettings;
        }
    }

    if (enabledSections.has("blobStorage")) {
        result.blobStorage = {
            provider: config.blobStorage.provider,
            url: strOrNull(config.blobStorage.url),
            connectionString: strOrNull(config.blobStorage.connectionString),
            accessToken: strOrNull(config.blobStorage.accessToken),
            container: strOrNull(config.blobStorage.container),
        };
    }

    if (enabledSections.has("settings")) {
        result.settings = {
            exportsOrganizationCode: config.settings.exportsOrganizationCode,
            organizationCodeExportFieldName: strOrNull(config.settings.organizationCodeExportFieldName),
            organizationCodeParseFieldName: strOrNull(config.settings.organizationCodeParseFieldName),
            organizationCodeParseFieldNameDescription: strOrNull(config.settings.organizationCodeParseFieldNameDescription),
            taskConfig: {
                vendorLookup: serializeVendorLookup(config.settings.taskConfig.vendorLookup),
                receiptLookup: {
                    vendorIdField: config.settings.taskConfig.receiptLookup.vendorIdField || "vendor_id",
                    invoiceDateField: config.settings.taskConfig.receiptLookup.invoiceDateField || "invoice_date",
                },
            },
        };
    }

    return result;
}

// =============================================================================
// HELPERS
// =============================================================================

/** Map bridge status string to badge color */
function statusBadgeColor(status: string): "success" | "gray" | "warning" | "error" {
    switch (status) {
        case "Online":
            return "success";
        case "Offline":
            return "gray";
        case "Connecting":
            return "warning";
        case "Error":
            return "error";
        default:
            return "gray";
    }
}

/** Format a timestamp to relative time */
function relativeTime(isoString: string | null): { text: string; isStale: boolean } {
    if (!isoString) return { text: "Never", isStale: true };
    const diff = Date.now() - new Date(isoString).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return { text: "Just now", isStale: false };
    if (minutes < 60) return { text: `${minutes}m ago`, isStale: minutes > 3 };
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return { text: `${hours}h ago`, isStale: true };
    const days = Math.floor(hours / 24);
    return { text: `${days}d ago`, isStale: true };
}

// =============================================================================
// REUSABLE FIELD COMPONENTS
// =============================================================================

function SectionHeader({ title, description }: { title: string; description?: string }) {
    return (
        <div className="border-b border-secondary pb-2">
            <h5 className="text-xs font-semibold text-primary">{title}</h5>
            {description && <p className="mt-0.5 text-xs text-tertiary">{description}</p>}
        </div>
    );
}

function FieldRow({
    label,
    hint,
    children,
    wideLabel = false,
    required = false,
}: {
    label: string;
    hint?: string;
    children: React.ReactNode;
    wideLabel?: boolean;
    required?: boolean;
}) {
    return (
        <div className={`grid items-start gap-3 ${wideLabel ? "grid-cols-[240px_1fr]" : "grid-cols-[180px_1fr]"}`}>
            <div className="pt-1.5">
                <label className={`text-xs font-medium text-secondary ${wideLabel ? "whitespace-nowrap" : ""}`}>
                    {label}
                    {required && <span className="ml-1 text-error-primary">*</span>}
                </label>
                {hint && <p className="mt-0.5 text-xs text-quaternary">{hint}</p>}
            </div>
            <div>{children}</div>
        </div>
    );
}

// =============================================================================
// COLLAPSIBLE SECTION (with optional remove button)
// =============================================================================

function CollapsibleSection({
    title,
    description,
    defaultOpen = false,
    onRemove,
    children,
}: {
    title: string;
    description?: string;
    defaultOpen?: boolean;
    onRemove?: () => void;
    children: React.ReactNode;
}) {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="rounded-lg border border-secondary">
            <div className="flex items-center justify-between px-4 py-3">
                <button className="flex flex-1 cursor-pointer items-center gap-2 text-left" onClick={() => setIsOpen(!isOpen)}>
                    <span className="text-xs text-tertiary">{isOpen ? "▾" : "▸"}</span>
                    <div>
                        <h5 className="text-xs font-semibold text-primary">{title}</h5>
                        {description && <p className="mt-0.5 text-xs text-tertiary">{description}</p>}
                    </div>
                </button>
                {onRemove && (
                    <Button color="tertiary-destructive" size="sm" onClick={onRemove}>
                        Remove
                    </Button>
                )}
            </div>
            {isOpen && <div className="space-y-3 border-t border-secondary px-4 py-3">{children}</div>}
        </div>
    );
}

// =============================================================================
// BRIDGE CONFIG EDITOR (structured, schema-driven, sections are optional)
// =============================================================================

function BridgeConfigEditor({ configJson, clientId, onSaved }: { configJson: string | null; clientId: number; onSaved: () => void }) {
    const adminApi = useAdminApi();

    const [config, setConfig] = useState<BridgeConfig>(() => parseRawConfig(configJson));
    const [enabledSections, setEnabledSections] = useState<Set<OptionalSectionKey>>(() => detectEnabledSections(configJson));
    const [showRawJson, setShowRawJson] = useState(false);
    const [rawJson, setRawJson] = useState("");

    // Save state
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Organizations for the domain-ID-map dropdown. Best-effort: a failure leaves the dropdown empty,
    // but existing rows (and their keys) stay editable.
    const [organizations, setOrganizations] = useState<OrganizationAdminListItemDto[]>([]);
    useEffect(() => {
        let cancelled = false;
        adminApi
            .listClientOrganizations(clientId)
            .then((orgs) => {
                if (!cancelled) setOrganizations(orgs);
            })
            .catch(() => {
                /* non-blocking */
            });
        return () => {
            cancelled = true;
        };
    }, [adminApi, clientId]);

    // Document types for the EZCM export-document-settings dropdown. Best-effort like organizations.
    const [documentTypes, setDocumentTypes] = useState<DocumentTypeAdminListItemDto[]>([]);
    useEffect(() => {
        let cancelled = false;
        adminApi
            .listDocumentTypes()
            .then((types) => {
                if (!cancelled) setDocumentTypes(types);
            })
            .catch(() => {
                /* non-blocking */
            });
        return () => {
            cancelled = true;
        };
    }, [adminApi]);

    // Sync from props
    useEffect(() => {
        setConfig(parseRawConfig(configJson));
        setEnabledSections(detectEnabledSections(configJson));
    }, [configJson]);

    // Derived: current serialized JSON for raw view
    const currentJson = useMemo(() => JSON.stringify(serializeConfig(config, enabledSections), null, 2), [config, enabledSections]);

    // Export document settings only apply to EZCM API exports — gate both the section render
    // and the Add-section dropdown entry on the current export target selection.
    const isEzcmApiExport = config.exportTarget.target === "ezcm" && config.exportTarget.type === "api";

    // Sections available to add (not yet enabled). exportDocSettings is additionally gated:
    // it only makes sense for EZCM/API exports and lives inside targetSystemAPISettings on the wire.
    const availableSections = useMemo(
        () =>
            OPTIONAL_SECTIONS.filter((s) => {
                if (enabledSections.has(s.key)) return false;
                if (s.key === "exportDocSettings") {
                    return isEzcmApiExport && enabledSections.has("targetSystemAPISettings");
                }
                return true;
            }),
        [enabledSections, isEzcmApiExport],
    );

    // AP divisions are a Sage 100-only concept (only the Sage 100 vendor-lookup connector resolves
    // a division). Gate the AP Divisions UI on the selected vendor-lookup target so it never shows
    // for GP / AIM / Sage 500 clients, where the flag would be dead config.
    const isSage100VendorLookup = config.vendorLookup.target === "sage100";

    // "custom" vendor lookup target → vendorMaster is configured per-organization (table/columns/
    // connection string) via a mapping table that replaces the single vendorMaster input.
    const isCustomVendorLookup = config.vendorLookup.target === "custom";

    // Select items for "Add Section" dropdown
    const addSectionItems = useMemo(() => availableSections.map((s) => ({ id: s.key, label: s.label, supportingText: s.description })), [availableSections]);

    // --- Section add/remove ---

    const addSection = useCallback((key: OptionalSectionKey) => {
        setEnabledSections((prev) => {
            const next = new Set(prev);
            next.add(key);
            return next;
        });
        // Reset the section to defaults when adding
        setConfig((prev) => {
            switch (key) {
                case "connectionStrings":
                    return { ...prev, connectionStrings: { ...DEFAULT_CONNECTION_STRINGS } };
                case "vendorMasterAPISettings":
                    return { ...prev, vendorMasterAPISettings: { ...DEFAULT_VENDOR_MASTER_API } };
                case "targetSystemAPISettings":
                    return { ...prev, targetSystemAPISettings: { ...DEFAULT_TARGET_SYSTEM_API } };
                case "exportDocSettings":
                    return { ...prev, targetSystemAPISettings: { ...prev.targetSystemAPISettings, exportDocSettings: [] } };
                case "blobStorage":
                    return { ...prev, blobStorage: { ...DEFAULT_BLOB } };
                case "settings":
                    return { ...prev, settings: structuredClone(DEFAULT_SETTINGS) };
                default:
                    return prev;
            }
        });
    }, []);

    const removeSection = useCallback((key: OptionalSectionKey) => {
        setEnabledSections((prev) => {
            const next = new Set(prev);
            next.delete(key);
            // exportDocSettings lives inside targetSystemAPISettings on the wire — removing the
            // parent section removes the child section too.
            if (key === "targetSystemAPISettings") {
                next.delete("exportDocSettings");
            }
            return next;
        });
    }, []);

    // --- Setters for nested fields ---

    const set = useCallback(<K extends keyof BridgeConfig>(key: K, value: BridgeConfig[K]) => {
        setConfig((prev) => ({ ...prev, [key]: value }));
    }, []);

    const setFeatureFlag = useCallback((flag: FeatureFlagKey, value: boolean) => {
        setConfig((prev) => ({
            ...prev,
            featureFlags: { ...prev.featureFlags, [flag]: value },
        }));
    }, []);

    const setConnectionString = useCallback((key: ConnectionStringKey, value: string) => {
        setConfig((prev) => ({
            ...prev,
            connectionStrings: { ...prev.connectionStrings, [key]: value },
        }));
    }, []);

    const setVendorMasterApi = useCallback((key: keyof BridgeConfig["vendorMasterAPISettings"], value: string) => {
        setConfig((prev) => ({
            ...prev,
            vendorMasterAPISettings: { ...prev.vendorMasterAPISettings, [key]: value },
        }));
    }, []);

    const setTargetSystemApi = useCallback((key: keyof BridgeConfig["targetSystemAPISettings"], value: string | boolean) => {
        setConfig((prev) => ({
            ...prev,
            targetSystemAPISettings: { ...prev.targetSystemAPISettings, [key]: value },
        }));
    }, []);

    // --- Custom vendor lookup row helpers (organization code → bespoke vendor table mapping) ---

    const addCustomVendorRow = useCallback(() => {
        setConfig((prev) => ({
            ...prev,
            customVendorLookup: [
                ...prev.customVendorLookup,
                { orgCode: "", connectionString: "", tableName: "", vendorIdColumn: "", vendorNameColumn: "", vendorPhoneColumn: "" },
            ],
        }));
    }, []);

    const updateCustomVendorRow = useCallback((index: number, key: keyof CustomVendorMapRow, value: string) => {
        setConfig((prev) => ({
            ...prev,
            customVendorLookup: prev.customVendorLookup.map((row, i) => (i === index ? { ...row, [key]: value } : row)),
        }));
    }, []);

    const removeCustomVendorRow = useCallback((index: number) => {
        setConfig((prev) => ({
            ...prev,
            customVendorLookup: prev.customVendorLookup.filter((_, i) => i !== index),
        }));
    }, []);

    // --- Domain ID map row helpers (organization code → EZCM domain ID) ---

    const addDomainIdRow = useCallback(() => {
        setConfig((prev) => ({
            ...prev,
            targetSystemAPISettings: {
                ...prev.targetSystemAPISettings,
                domainIDMap: [...prev.targetSystemAPISettings.domainIDMap, { orgCode: "", domainId: "", apDivisionsEnabled: false }],
            },
        }));
    }, []);

    const updateDomainIdRow = useCallback((index: number, key: keyof DomainIdMapRow, value: string | boolean) => {
        setConfig((prev) => ({
            ...prev,
            targetSystemAPISettings: {
                ...prev.targetSystemAPISettings,
                domainIDMap: prev.targetSystemAPISettings.domainIDMap.map((row, i) => (i === index ? { ...row, [key]: value } : row)),
            },
        }));
    }, []);

    const removeDomainIdRow = useCallback((index: number) => {
        setConfig((prev) => ({
            ...prev,
            targetSystemAPISettings: {
                ...prev.targetSystemAPISettings,
                domainIDMap: prev.targetSystemAPISettings.domainIDMap.filter((_, i) => i !== index),
            },
        }));
    }, []);

    // --- EZCM export document settings row helpers (orgCode + docTypeCode → targetFolder/convertPdf) ---

    const addExportDocRow = useCallback(() => {
        setConfig((prev) => ({
            ...prev,
            targetSystemAPISettings: {
                ...prev.targetSystemAPISettings,
                exportDocSettings: [...prev.targetSystemAPISettings.exportDocSettings, { orgCode: "", docTypeCode: "", targetFolder: "", convertPdf: "none" }],
            },
        }));
    }, []);

    const updateExportDocRow = useCallback((index: number, key: keyof ExportDocSettingRow, value: string) => {
        setConfig((prev) => ({
            ...prev,
            targetSystemAPISettings: {
                ...prev.targetSystemAPISettings,
                exportDocSettings: prev.targetSystemAPISettings.exportDocSettings.map((row, i) => (i === index ? { ...row, [key]: value } : row)),
            },
        }));
    }, []);

    const removeExportDocRow = useCallback((index: number) => {
        setConfig((prev) => ({
            ...prev,
            targetSystemAPISettings: {
                ...prev.targetSystemAPISettings,
                exportDocSettings: prev.targetSystemAPISettings.exportDocSettings.filter((_, i) => i !== index),
            },
        }));
    }, []);

    const setBlob = useCallback((key: keyof BridgeConfig["blobStorage"], value: string) => {
        setConfig((prev) => ({
            ...prev,
            blobStorage: { ...prev.blobStorage, [key]: value },
        }));
    }, []);

    const setVendorLookupTarget = useCallback((key: keyof BridgeConfig["vendorLookup"], value: string) => {
        setConfig((prev) => ({
            ...prev,
            vendorLookup: { ...prev.vendorLookup, [key]: value },
        }));
    }, []);

    const setExportTargetConfig = useCallback((key: keyof BridgeConfig["exportTarget"], value: string) => {
        setConfig((prev) => ({
            ...prev,
            exportTarget: { ...prev.exportTarget, [key]: value },
        }));
    }, []);

    const setVendorLookupTask = useCallback((key: keyof BridgeConfig["settings"]["taskConfig"]["vendorLookup"], value: string) => {
        setConfig((prev) => ({
            ...prev,
            settings: {
                ...prev.settings,
                taskConfig: {
                    ...prev.settings.taskConfig,
                    vendorLookup: { ...prev.settings.taskConfig.vendorLookup, [key]: value },
                },
            },
        }));
    }, []);

    const setVendorLookupTaskBool = useCallback((key: "searchCheckName" | "searchShortName", value: boolean) => {
        setConfig((prev) => ({
            ...prev,
            settings: {
                ...prev.settings,
                taskConfig: {
                    ...prev.settings.taskConfig,
                    vendorLookup: { ...prev.settings.taskConfig.vendorLookup, [key]: value },
                },
            },
        }));
    }, []);

    const setReceiptLookupTask = useCallback((key: keyof BridgeConfig["settings"]["taskConfig"]["receiptLookup"], value: string) => {
        setConfig((prev) => ({
            ...prev,
            settings: {
                ...prev.settings,
                taskConfig: {
                    ...prev.settings.taskConfig,
                    receiptLookup: { ...prev.settings.taskConfig.receiptLookup, [key]: value },
                },
            },
        }));
    }, []);

    const setExportsOrganizationCode = useCallback((value: boolean) => {
        setConfig((prev) => ({ ...prev, settings: { ...prev.settings, exportsOrganizationCode: value } }));
    }, []);

    const setOrganizationCodeExportFieldName = useCallback((value: string) => {
        setConfig((prev) => ({ ...prev, settings: { ...prev.settings, organizationCodeExportFieldName: value } }));
    }, []);

    const setOrganizationCodeParseFieldName = useCallback((value: string) => {
        setConfig((prev) => ({ ...prev, settings: { ...prev.settings, organizationCodeParseFieldName: value } }));
    }, []);

    const setOrganizationCodeParseFieldNameDescription = useCallback((value: string) => {
        setConfig((prev) => ({ ...prev, settings: { ...prev.settings, organizationCodeParseFieldNameDescription: value } }));
    }, []);

    // --- Save ---

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        setSaveError(null);
        setSaveSuccess(false);

        let bridgeConfigValue: unknown;
        if (showRawJson) {
            const trimmed = rawJson.trim();
            if (!trimmed) {
                bridgeConfigValue = null;
            } else {
                try {
                    bridgeConfigValue = JSON.parse(trimmed);
                } catch {
                    setSaveError("Bridge config must be valid JSON.");
                    setIsSaving(false);
                    return;
                }
            }
        } else {
            // Domain ID is required for every mapping and must be a valid GUID (blank is not allowed).
            if (enabledSections.has("targetSystemAPISettings")) {
                const bad = config.targetSystemAPISettings.domainIDMap.find((r) => !isValidGuid(r.domainId));
                if (bad) {
                    setSaveError("Each Domain ID is required and must be a valid GUID. Fix or remove any empty/invalid row.");
                    setIsSaving(false);
                    return;
                }
            }
            // Custom per-org vendor lookup: organization, connection string, table, and the id/name
            // columns are all required for every mapping (the phone column stays optional). Blank
            // rows can't be silently dropped — surface them so the operator fixes or removes them.
            if (enabledSections.has("connectionStrings") && config.vendorLookup.target === "custom") {
                const incomplete = config.customVendorLookup.some(
                    (r) =>
                        r.orgCode.trim() === "" ||
                        r.connectionString.trim() === "" ||
                        r.tableName.trim() === "" ||
                        r.vendorIdColumn.trim() === "" ||
                        r.vendorNameColumn.trim() === "",
                );
                if (incomplete) {
                    setSaveError(
                        "Each custom vendor mapping requires an organization, connection string, table name, vendor ID column, and vendor name column. Fill in or remove any incomplete mapping.",
                    );
                    setIsSaving(false);
                    return;
                }
            }
            bridgeConfigValue = serializeConfig(config, enabledSections);
        }

        try {
            await adminApi.updateClientSettings(clientId, {
                settings: { BridgeConfig: bridgeConfigValue },
            });
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
            onSaved();
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to save bridge config.");
        } finally {
            setIsSaving(false);
        }
    }, [showRawJson, rawJson, config, enabledSections, adminApi, clientId, onSaved]);

    // --- Vendor/Export target items for Select ---
    const vendorLookupTargetItems = useMemo(
        () => [
            { id: "", label: "\u2014 None \u2014" },
            { id: "sage100", label: "Sage 100" },
            { id: "sage500", label: "Sage 500" },
            { id: "aim", label: "AIM Vision" },
            { id: "gp", label: "Dynamics GP" },
            { id: "custom", label: "Custom per organization" },
        ],
        [],
    );
    const exportTargetItems = useMemo(
        () => [
            { id: "", label: "\u2014 None \u2014" },
            { id: "ezcm", label: "EZCM" },
            { id: "aim", label: "AIM Vision" },
            { id: "gp", label: "Dynamics GP" },
        ],
        [],
    );
    const targetTypeItems = useMemo(
        () => [
            { id: "", label: "\u2014 None \u2014" },
            { id: "api", label: "API" },
            { id: "db", label: "Database" },
        ],
        [],
    );

    // --- Blob provider items for Select ---
    const blobProviderItems = useMemo(() => BLOB_PROVIDERS.map((p) => ({ id: p, label: p })), []);

    // --- Render ---

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <SectionHeader title="Bridge Configuration" description="Client-level bridge runtime configuration." />
                <button
                    className="cursor-pointer text-xs font-medium text-brand-primary hover:underline"
                    onClick={() => {
                        if (!showRawJson) {
                            setRawJson(currentJson);
                        } else {
                            try {
                                const parsed = JSON.parse(rawJson);
                                const jsonStr = JSON.stringify(parsed);
                                setConfig(parseRawConfig(jsonStr));
                                setEnabledSections(detectEnabledSections(jsonStr));
                            } catch {
                                /* stay in raw mode if invalid */
                            }
                        }
                        setShowRawJson(!showRawJson);
                    }}
                >
                    {showRawJson ? "Structured Editor" : "Raw JSON"}
                </button>
            </div>

            {showRawJson ? (
                <textarea
                    className="focus:border-brand-primary w-full rounded-lg border border-secondary bg-primary p-3 font-mono text-xs text-primary focus:outline-none"
                    value={rawJson}
                    onChange={(e) => setRawJson(e.target.value)}
                    rows={24}
                    spellCheck={false}
                />
            ) : (
                <div className="space-y-3">
                    {/* ---- Bridge Identity (always present) ---- */}
                    <CollapsibleSection title="Bridge Identity" defaultOpen>
                        <FieldRow label="Name" hint="Human-readable bridge name">
                            <Input value={config.name} onChange={(v) => set("name", v)} placeholder="Production Bridge" size="sm" />
                        </FieldRow>
                        <FieldRow label="Enabled">
                            <Toggle size="sm" isSelected={config.isEnabled} onChange={(v) => set("isEnabled", v)} />
                        </FieldRow>
                    </CollapsibleSection>

                    {/* ---- Polling Configuration (always present) ---- */}
                    <CollapsibleSection title="Polling Configuration" description="Controls how often the bridge polls for work.">
                        <FieldRow label="Poll Interval (ms)" hint="Default: 15000">
                            <Input value={config.pollIntervalMs} onChange={(v) => set("pollIntervalMs", v)} placeholder="15000" size="sm" />
                        </FieldRow>
                        <FieldRow label="Max Concurrent Jobs" hint="Default: 5">
                            <Input value={config.maxConcurrentJobs} onChange={(v) => set("maxConcurrentJobs", v)} placeholder="5" size="sm" />
                        </FieldRow>
                        <FieldRow label="Max Jobs Per Cycle" hint="Default: 10">
                            <Input value={config.maxJobsPerCycle} onChange={(v) => set("maxJobsPerCycle", v)} placeholder="10" size="sm" />
                        </FieldRow>
                        <FieldRow label="Config Refresh (ms)" hint="Default: 300000 (5 min)">
                            <Input value={config.configRefreshIntervalMs} onChange={(v) => set("configRefreshIntervalMs", v)} placeholder="300000" size="sm" />
                        </FieldRow>
                    </CollapsibleSection>

                    {/* ---- Feature Flags (always present) ---- */}
                    <CollapsibleSection title="Feature Flags" description="Enable or disable bridge capabilities.">
                        {FEATURE_FLAG_KEYS.map((flag) => (
                            <FieldRow key={flag} label={flag}>
                                <Toggle size="sm" isSelected={config.featureFlags[flag]} onChange={(v) => setFeatureFlag(flag, v)} />
                            </FieldRow>
                        ))}
                    </CollapsibleSection>

                    {/* ---- Telemetry (always present) ---- */}
                    <CollapsibleSection title="Telemetry" description="Application Insights configuration for bridge monitoring.">
                        <FieldRow label="App Insights Connection String" hint="Connection string from Azure Portal. Leave empty to disable.">
                            <Input
                                value={config.applicationInsightsConnectionString}
                                onChange={(v) => set("applicationInsightsConnectionString", v)}
                                placeholder="InstrumentationKey=...;IngestionEndpoint=..."
                                size="sm"
                            />
                        </FieldRow>
                    </CollapsibleSection>

                    {/* ---- Vendor / Export Settings (always present) ---- */}
                    <CollapsibleSection title="Vendor & Export Settings">
                        <FieldRow label="Vendor Lookup Target" hint="ERP system for vendor lookups">
                            <Select
                                items={vendorLookupTargetItems}
                                selectedKey={config.vendorLookup.target}
                                onSelectionChange={(key) => setVendorLookupTarget("target", String(key))}
                                placeholder="Select target"
                                size="sm"
                            >
                                {(item) => <Select.Item {...item} />}
                            </Select>
                        </FieldRow>
                        <FieldRow label="Vendor Lookup Type" hint="Connection type (API or Database)">
                            <Select
                                items={targetTypeItems}
                                selectedKey={config.vendorLookup.type}
                                onSelectionChange={(key) => setVendorLookupTarget("type", String(key))}
                                placeholder="Select type"
                                size="sm"
                            >
                                {(item) => <Select.Item {...item} />}
                            </Select>
                        </FieldRow>
                        <FieldRow label="Export Target" hint="Target system for exports">
                            <Select
                                items={exportTargetItems}
                                selectedKey={config.exportTarget.target}
                                onSelectionChange={(key) => setExportTargetConfig("target", String(key))}
                                placeholder="Select target"
                                size="sm"
                            >
                                {(item) => <Select.Item {...item} />}
                            </Select>
                        </FieldRow>
                        <FieldRow label="Export Target Type" hint="Connection type (API or Database)">
                            <Select
                                items={targetTypeItems}
                                selectedKey={config.exportTarget.type}
                                onSelectionChange={(key) => setExportTargetConfig("type", String(key))}
                                placeholder="Select type"
                                size="sm"
                            >
                                {(item) => <Select.Item {...item} />}
                            </Select>
                        </FieldRow>
                        <FieldRow label="Vendors to Return" hint="Number of vendor matches (default: 2)">
                            <Input value={config.vendorsToReturn} onChange={(v) => set("vendorsToReturn", v)} placeholder="2" size="sm" />
                        </FieldRow>
                        <FieldRow label="Custom Field Separator" hint="Pipe-delimited separator (default: |)">
                            <Input value={config.exportedAsCustomSeparator} onChange={(v) => set("exportedAsCustomSeparator", v)} placeholder="|" size="sm" />
                        </FieldRow>
                    </CollapsibleSection>

                    {/* ---- Optional Sections (only rendered if enabled) ---- */}

                    {enabledSections.has("connectionStrings") && (
                        <CollapsibleSection
                            title="Connection Strings"
                            description="SQL Server connection strings for target ERP databases."
                            onRemove={() => removeSection("connectionStrings")}
                        >
                            {CONNECTION_STRING_KEYS.map((key) => {
                                // For the "custom" vendor lookup target the vendor master is configured
                                // per-organization (table/columns/connection string), so replace the single
                                // vendorMaster input with the per-org mapping table. autoSys/targetSystem
                                // are unaffected.
                                if (key === "vendorMaster" && isCustomVendorLookup) {
                                    return (
                                        <div key={key} className="space-y-2">
                                            <SectionHeader
                                                title="Vendor Master (per organization)"
                                                description="Each organization maps to its own vendor table. The bridge builds the same fuzzy-search query (LIKE → approximate → fuzzy) against the configured table and columns. The phone column is optional."
                                            />
                                            {config.customVendorLookup.length === 0 ? (
                                                <p className="py-1 text-xs text-quaternary">No mappings yet. Add one below.</p>
                                            ) : (
                                                <div className="space-y-3">
                                                    {config.customVendorLookup.map((row, index) => {
                                                        // Org codes chosen in other rows — excluded to prevent duplicate keys.
                                                        const takenByOthers = new Set(
                                                            config.customVendorLookup
                                                                .filter((_, i) => i !== index)
                                                                .map((r) => r.orgCode)
                                                                .filter((c) => c !== ""),
                                                        );
                                                        const orgItems = organizations
                                                            .filter((o) => !takenByOthers.has(o.code))
                                                            .map((o) => ({ id: o.code, label: `${o.code} — ${o.name}` }));
                                                        // Preserve this row's current key even if it isn't a known org (e.g. legacy/seed keys).
                                                        if (row.orgCode && !orgItems.some((it) => it.id === row.orgCode)) {
                                                            orgItems.unshift({ id: row.orgCode, label: row.orgCode });
                                                        }
                                                        return (
                                                            <div key={index} className="space-y-3 rounded-lg border border-secondary p-4">
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-xs font-semibold text-primary">{row.orgCode || "New mapping"}</span>
                                                                    <Button
                                                                        color="tertiary-destructive"
                                                                        size="sm"
                                                                        iconLeading={Trash01}
                                                                        onClick={() => removeCustomVendorRow(index)}
                                                                        aria-label="Remove mapping"
                                                                    />
                                                                </div>
                                                                <FieldRow label="Organization" wideLabel required>
                                                                    <Select
                                                                        items={orgItems}
                                                                        selectedKey={row.orgCode || null}
                                                                        onSelectionChange={(k) => updateCustomVendorRow(index, "orgCode", String(k))}
                                                                        placeholder="Select organization"
                                                                        size="sm"
                                                                    >
                                                                        {(item) => <Select.Item {...item} />}
                                                                    </Select>
                                                                </FieldRow>
                                                                <FieldRow
                                                                    label="Database Connection String"
                                                                    hint="SQL Server connection string for this org's vendor database"
                                                                    wideLabel
                                                                    required
                                                                >
                                                                    <Input
                                                                        value={row.connectionString}
                                                                        onChange={(v) => updateCustomVendorRow(index, "connectionString", v)}
                                                                        placeholder="Server=...;Database=...;"
                                                                        size="sm"
                                                                        isInvalid={row.connectionString.trim() === ""}
                                                                    />
                                                                </FieldRow>
                                                                <FieldRow label="Vendor Table Name" hint="Table to query (e.g. dbo.PM00200)" wideLabel required>
                                                                    <Input
                                                                        value={row.tableName}
                                                                        onChange={(v) => updateCustomVendorRow(index, "tableName", v)}
                                                                        placeholder="dbo.vendor_master"
                                                                        size="sm"
                                                                        isInvalid={row.tableName.trim() === ""}
                                                                    />
                                                                </FieldRow>
                                                                <FieldRow
                                                                    label="Vendor ID Column"
                                                                    hint="Column holding the vendor ID (e.g. VENDORID)"
                                                                    wideLabel
                                                                    required
                                                                >
                                                                    <Input
                                                                        value={row.vendorIdColumn}
                                                                        onChange={(v) => updateCustomVendorRow(index, "vendorIdColumn", v)}
                                                                        placeholder="vendor_id"
                                                                        size="sm"
                                                                        isInvalid={row.vendorIdColumn.trim() === ""}
                                                                    />
                                                                </FieldRow>
                                                                <FieldRow
                                                                    label="Vendor Name Column"
                                                                    hint="Column searched for the vendor name (e.g. VENDNAME)"
                                                                    wideLabel
                                                                    required
                                                                >
                                                                    <Input
                                                                        value={row.vendorNameColumn}
                                                                        onChange={(v) => updateCustomVendorRow(index, "vendorNameColumn", v)}
                                                                        placeholder="vendor_name"
                                                                        size="sm"
                                                                        isInvalid={row.vendorNameColumn.trim() === ""}
                                                                    />
                                                                </FieldRow>
                                                                <FieldRow
                                                                    label="Phone Number Column"
                                                                    hint="Optional — column for phone-fallback search"
                                                                    wideLabel
                                                                >
                                                                    <Input
                                                                        value={row.vendorPhoneColumn}
                                                                        onChange={(v) => updateCustomVendorRow(index, "vendorPhoneColumn", v)}
                                                                        placeholder="(optional)"
                                                                        size="sm"
                                                                    />
                                                                </FieldRow>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            <Button color="secondary" size="sm" iconLeading={Plus} onClick={addCustomVendorRow}>
                                                Add mapping
                                            </Button>
                                        </div>
                                    );
                                }
                                return (
                                    <FieldRow
                                        key={key}
                                        label={key}
                                        hint={
                                            key === "vendorMaster"
                                                ? "Vendor Master database"
                                                : key === "autoSys"
                                                  ? "AutoSys database (AIM/LTC)"
                                                  : "Target System database"
                                        }
                                    >
                                        <Input
                                            value={config.connectionStrings[key]}
                                            onChange={(v) => setConnectionString(key, v)}
                                            placeholder="Server=...;Database=...;User Id=...;Password=...;"
                                            size="sm"
                                        />
                                        {key === "vendorMaster" && config.vendorLookup.target === "gp" && (
                                            <p className="mt-1 text-xs text-tertiary">
                                                For Dynamics GP, set the database name to{" "}
                                                <code className="rounded bg-quaternary px-1 py-0.5 font-mono text-xs">{"{OrganizationCode}"}</code> (e.g.{" "}
                                                <code className="rounded bg-quaternary px-1 py-0.5 font-mono text-xs">{"Database={OrganizationCode}"}</code>)
                                                when each organization has its own ERP company database. The bridge substitutes it with the document's
                                                organization code at lookup time.
                                            </p>
                                        )}
                                    </FieldRow>
                                );
                            })}
                        </CollapsibleSection>
                    )}

                    {enabledSections.has("vendorMasterAPISettings") && (
                        <CollapsibleSection
                            title="Vendor Master API"
                            description="Vendor master API settings (e.g. Sage 100 BOI Wrapper)."
                            onRemove={() => removeSection("vendorMasterAPISettings")}
                        >
                            <FieldRow label="Base URL">
                                <Input
                                    value={config.vendorMasterAPISettings.baseUrl}
                                    onChange={(v) => setVendorMasterApi("baseUrl", v)}
                                    placeholder="http://sage-server:8045"
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow label="Version Path" hint="Default: /tables/">
                                <Input
                                    value={config.vendorMasterAPISettings.versionPath}
                                    onChange={(v) => setVendorMasterApi("versionPath", v)}
                                    placeholder="/tables/"
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow label="Vendor Service Name" hint="Default: ap_vendor_svc">
                                <Input
                                    value={config.vendorMasterAPISettings.vendorServiceName}
                                    onChange={(v) => setVendorMasterApi("vendorServiceName", v)}
                                    placeholder="ap_vendor_svc"
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow label="Auth Key">
                                <Input
                                    value={config.vendorMasterAPISettings.authKey}
                                    onChange={(v) => setVendorMasterApi("authKey", v)}
                                    placeholder="API auth key"
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow label="Auth Secret">
                                <Input
                                    value={config.vendorMasterAPISettings.authSecret}
                                    onChange={(v) => setVendorMasterApi("authSecret", v)}
                                    placeholder="API auth secret"
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow label="Module" hint="Default: A/P">
                                <Input
                                    value={config.vendorMasterAPISettings.module}
                                    onChange={(v) => setVendorMasterApi("module", v)}
                                    placeholder="A/P"
                                    size="sm"
                                />
                            </FieldRow>
                        </CollapsibleSection>
                    )}

                    {enabledSections.has("targetSystemAPISettings") && (
                        <CollapsibleSection
                            title="Target System API"
                            description="Target system REST API settings (e.g. EZCM)."
                            onRemove={() => removeSection("targetSystemAPISettings")}
                        >
                            <FieldRow label="Base URL">
                                <Input
                                    value={config.targetSystemAPISettings.baseUrl}
                                    onChange={(v) => setTargetSystemApi("baseUrl", v)}
                                    placeholder="http://ezcm-server:8080/ezcm"
                                    size="sm"
                                />
                            </FieldRow>
                            {/* Domain ID Map — per-organization export routing (orgCode → EZCM domain ID) */}
                            <div className="space-y-2">
                                <SectionHeader
                                    title="Domain ID Map"
                                    description={
                                        isSage100VendorLookup
                                            ? "Maps each organization to its EZCM domain ID and AP divisions setting. Exports route to the domain matched by the document's organization code; AP Divisions, when enabled, prefixes exported vendor IDs with the vendor's AP division for that organization."
                                            : "Maps each organization to its EZCM domain ID. Exports route to the domain matched by the document's organization code."
                                    }
                                />
                                {config.targetSystemAPISettings.domainIDMap.length === 0 ? (
                                    <p className="py-1 text-xs text-quaternary">No mappings yet. Add one below.</p>
                                ) : (
                                    <div className="overflow-hidden rounded-lg border border-secondary">
                                        <table className="w-full text-left text-sm">
                                            <thead className="border-b border-secondary bg-secondary_subtle">
                                                <tr>
                                                    <th className="px-3 py-2 text-xs font-medium text-tertiary">Organization</th>
                                                    <th className="px-3 py-2 text-xs font-medium text-tertiary">Domain ID</th>
                                                    {isSage100VendorLookup && (
                                                        <th className="w-32 px-3 py-2 text-xs font-medium text-tertiary">AP Divisions</th>
                                                    )}
                                                    <th className="w-10 px-3 py-2"></th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-secondary">
                                                {config.targetSystemAPISettings.domainIDMap.map((row, index) => {
                                                    // Org codes chosen in other rows — excluded here to prevent duplicate keys.
                                                    const takenByOthers = new Set(
                                                        config.targetSystemAPISettings.domainIDMap
                                                            .filter((_, i) => i !== index)
                                                            .map((r) => r.orgCode)
                                                            .filter((c) => c !== ""),
                                                    );
                                                    const orgItems = organizations
                                                        .filter((o) => !takenByOthers.has(o.code))
                                                        .map((o) => ({ id: o.code, label: `${o.code} — ${o.name}` }));
                                                    // Preserve this row's current key even if it isn't a known org (e.g. legacy/seed keys).
                                                    if (row.orgCode && !orgItems.some((it) => it.id === row.orgCode)) {
                                                        orgItems.unshift({ id: row.orgCode, label: row.orgCode });
                                                    }
                                                    // Domain ID is required and must be a valid GUID (blank is not allowed).
                                                    const domainIdInvalid = !isValidGuid(row.domainId);
                                                    return (
                                                        <tr key={index}>
                                                            <td className="px-3 py-2 align-top">
                                                                <Select
                                                                    items={orgItems}
                                                                    selectedKey={row.orgCode || null}
                                                                    onSelectionChange={(key) => updateDomainIdRow(index, "orgCode", String(key))}
                                                                    placeholder="Select organization"
                                                                    size="sm"
                                                                >
                                                                    {(item) => <Select.Item {...item} />}
                                                                </Select>
                                                            </td>
                                                            <td className="px-3 py-2 align-top">
                                                                <Input
                                                                    value={row.domainId}
                                                                    onChange={(v) => updateDomainIdRow(index, "domainId", v)}
                                                                    placeholder="GUID (required)"
                                                                    size="sm"
                                                                    isInvalid={domainIdInvalid}
                                                                />
                                                                {domainIdInvalid && (
                                                                    <p className="mt-1 text-xs text-error-primary">
                                                                        Required — must be a valid GUID (e.g. 550e8400-e29b-41d4-a716-446655440000).
                                                                    </p>
                                                                )}
                                                            </td>
                                                            {isSage100VendorLookup && (
                                                                <td className="px-3 py-2 align-top">
                                                                    <Toggle
                                                                        size="sm"
                                                                        isSelected={row.apDivisionsEnabled}
                                                                        onChange={(v) => updateDomainIdRow(index, "apDivisionsEnabled", v)}
                                                                        aria-label="AP divisions enabled for this organization"
                                                                    />
                                                                </td>
                                                            )}
                                                            <td className="px-3 py-2 align-top">
                                                                <Button
                                                                    color="tertiary-destructive"
                                                                    size="sm"
                                                                    iconLeading={Trash01}
                                                                    onClick={() => removeDomainIdRow(index)}
                                                                    aria-label="Remove mapping"
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                <Button color="secondary" size="sm" iconLeading={Plus} onClick={addDomainIdRow}>
                                    Add mapping
                                </Button>
                            </div>
                            <FieldRow label="Client ID" hint="OAuth client ID (GUID)">
                                <Input
                                    value={config.targetSystemAPISettings.clientID}
                                    onChange={(v) => setTargetSystemApi("clientID", v)}
                                    placeholder="GUID"
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow label="Client Secret" hint="OAuth client secret">
                                <Input
                                    value={config.targetSystemAPISettings.clientSecret}
                                    onChange={(v) => setTargetSystemApi("clientSecret", v)}
                                    placeholder="Secret"
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow label="Username">
                                <Input
                                    value={config.targetSystemAPISettings.userName}
                                    onChange={(v) => setTargetSystemApi("userName", v)}
                                    placeholder="system"
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow label="Password">
                                <Input
                                    value={config.targetSystemAPISettings.password}
                                    onChange={(v) => setTargetSystemApi("password", v)}
                                    placeholder="Password"
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow label="Timeout (seconds)" hint="Default: 600">
                                <Input
                                    value={config.targetSystemAPISettings.timeoutSeconds}
                                    onChange={(v) => setTargetSystemApi("timeoutSeconds", v)}
                                    placeholder="600"
                                    size="sm"
                                />
                            </FieldRow>
                        </CollapsibleSection>
                    )}

                    {/* EZCM Export Document Settings — only meaningful for EZCM API exports; hidden (and
                        not serialized) when the export target is anything else. */}
                    {enabledSections.has("exportDocSettings") && enabledSections.has("targetSystemAPISettings") && isEzcmApiExport && (
                        <CollapsibleSection
                            title="EZCM Export Document Settings"
                            description="Per-organization, per-document-type target folder and PDF conversion applied on EZCM upload."
                            onRemove={() => removeSection("exportDocSettings")}
                        >
                            <div className="space-y-2">
                                {config.targetSystemAPISettings.exportDocSettings.length === 0 ? (
                                    <p className="py-1 text-xs text-quaternary">No settings yet. Add one below.</p>
                                ) : (
                                    <div className="overflow-hidden rounded-lg border border-secondary">
                                        <table className="w-full text-left text-sm">
                                            <thead className="border-b border-secondary bg-secondary_subtle">
                                                <tr>
                                                    <th className="px-3 py-2 text-xs font-medium text-tertiary">Organization</th>
                                                    <th className="px-3 py-2 text-xs font-medium text-tertiary">Document Type</th>
                                                    <th className="px-3 py-2 text-xs font-medium text-tertiary">Target Folder</th>
                                                    <th className="w-36 px-3 py-2 text-xs font-medium text-tertiary">Convert PDF</th>
                                                    <th className="w-10 px-3 py-2"></th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-secondary">
                                                {config.targetSystemAPISettings.exportDocSettings.map((row, index) => {
                                                    // (org, docType) pairs taken by other rows — exclude the matching doc types
                                                    // for the selected org to prevent duplicate keys.
                                                    const takenDocTypes = new Set(
                                                        config.targetSystemAPISettings.exportDocSettings
                                                            .filter((r, i) => i !== index && r.orgCode !== "" && r.orgCode === row.orgCode)
                                                            .map((r) => r.docTypeCode)
                                                            .filter((c) => c !== ""),
                                                    );
                                                    const orgItems = organizations.map((o) => ({ id: o.code, label: `${o.code} — ${o.name}` }));
                                                    // Preserve this row's current key even if it isn't a known org (e.g. legacy/seed keys).
                                                    if (row.orgCode && !orgItems.some((it) => it.id === row.orgCode)) {
                                                        orgItems.unshift({ id: row.orgCode, label: row.orgCode });
                                                    }
                                                    const docTypeItems = documentTypes
                                                        .filter((dt) => dt.isActive)
                                                        .map((dt) => ({ id: dt.code.toLowerCase(), label: `${dt.code} — ${dt.name}` }))
                                                        .filter((it) => !takenDocTypes.has(it.id));
                                                    // Preserve this row's current doc type even if it isn't a known type.
                                                    if (row.docTypeCode && !docTypeItems.some((it) => it.id === row.docTypeCode)) {
                                                        docTypeItems.unshift({ id: row.docTypeCode, label: row.docTypeCode });
                                                    }
                                                    return (
                                                        <tr key={index}>
                                                            <td className="px-3 py-2 align-top">
                                                                <Select
                                                                    items={orgItems}
                                                                    selectedKey={row.orgCode || null}
                                                                    onSelectionChange={(key) => updateExportDocRow(index, "orgCode", String(key))}
                                                                    placeholder="Select organization"
                                                                    size="sm"
                                                                >
                                                                    {(item) => <Select.Item {...item} />}
                                                                </Select>
                                                            </td>
                                                            <td className="px-3 py-2 align-top">
                                                                <Select
                                                                    items={docTypeItems}
                                                                    selectedKey={row.docTypeCode || null}
                                                                    onSelectionChange={(key) => updateExportDocRow(index, "docTypeCode", String(key))}
                                                                    placeholder="Select document type"
                                                                    size="sm"
                                                                >
                                                                    {(item) => <Select.Item {...item} />}
                                                                </Select>
                                                            </td>
                                                            <td className="px-3 py-2 align-top">
                                                                <Input
                                                                    value={row.targetFolder}
                                                                    onChange={(v) => updateExportDocRow(index, "targetFolder", v)}
                                                                    placeholder="EZCM folder (optional)"
                                                                    size="sm"
                                                                />
                                                            </td>
                                                            <td className="px-3 py-2 align-top">
                                                                <Select
                                                                    items={[...CONVERT_PDF_OPTIONS]}
                                                                    selectedKey={row.convertPdf || "none"}
                                                                    onSelectionChange={(key) => updateExportDocRow(index, "convertPdf", String(key))}
                                                                    size="sm"
                                                                >
                                                                    {(item) => <Select.Item {...item} />}
                                                                </Select>
                                                            </td>
                                                            <td className="px-3 py-2 align-top">
                                                                <Button
                                                                    color="tertiary-destructive"
                                                                    size="sm"
                                                                    iconLeading={Trash01}
                                                                    onClick={() => removeExportDocRow(index)}
                                                                    aria-label="Remove setting"
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                <Button color="secondary" size="sm" iconLeading={Plus} onClick={addExportDocRow}>
                                    Add setting
                                </Button>
                            </div>
                        </CollapsibleSection>
                    )}

                    {enabledSections.has("blobStorage") && (
                        <CollapsibleSection
                            title="Blob Storage"
                            description="Azure Blob or Vercel Blob configuration."
                            onRemove={() => removeSection("blobStorage")}
                        >
                            <FieldRow label="Provider">
                                <Select
                                    items={blobProviderItems}
                                    selectedKey={config.blobStorage.provider}
                                    onSelectionChange={(key) => setBlob("provider", String(key))}
                                    placeholder="Select provider"
                                    size="sm"
                                >
                                    {(item) => <Select.Item {...item} />}
                                </Select>
                            </FieldRow>
                            <FieldRow label="URL">
                                <Input
                                    value={config.blobStorage.url}
                                    onChange={(v) => setBlob("url", v)}
                                    placeholder="https://storageaccount.blob.core.windows.net"
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow label="Connection String" hint="Required if provider is Azure">
                                <Input
                                    value={config.blobStorage.connectionString}
                                    onChange={(v) => setBlob("connectionString", v)}
                                    placeholder="DefaultEndpointsProtocol=https;..."
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow label="Access Token" hint="Required if provider is Vercel">
                                <Input
                                    value={config.blobStorage.accessToken}
                                    onChange={(v) => setBlob("accessToken", v)}
                                    placeholder="Vercel Blob token"
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow label="Container" hint="Default: invoices">
                                <Input value={config.blobStorage.container} onChange={(v) => setBlob("container", v)} placeholder="invoices" size="sm" />
                            </FieldRow>
                        </CollapsibleSection>
                    )}

                    {enabledSections.has("settings") && (
                        <CollapsibleSection
                            title="Task Configuration"
                            description="Custom field codes for vendor and receipt lookup tasks, and export settings."
                            onRemove={() => removeSection("settings")}
                        >
                            <SectionHeader title="Export Settings" />
                            <FieldRow label="Export Organization Code" hint="When enabled, includes the document's organization code in every export payload">
                                <Toggle isSelected={config.settings.exportsOrganizationCode} onChange={setExportsOrganizationCode} />
                            </FieldRow>
                            {config.settings.exportsOrganizationCode && (
                                <FieldRow label="Organization Code Field Name" hint="Target system column/field name (e.g. CompanyID)">
                                    <Input
                                        value={config.settings.organizationCodeExportFieldName}
                                        onChange={setOrganizationCodeExportFieldName}
                                        placeholder="CompanyID"
                                        size="sm"
                                    />
                                </FieldRow>
                            )}

                            <SectionHeader title="Parse Settings" />
                            <FieldRow label="Org Code Parse Field" hint="Field code injected into LLM schema to extract the organization code during parsing">
                                <Input
                                    value={config.settings.organizationCodeParseFieldName}
                                    onChange={setOrganizationCodeParseFieldName}
                                    placeholder="store_number"
                                    size="sm"
                                />
                            </FieldRow>
                            {config.settings.organizationCodeParseFieldName && (
                                <FieldRow
                                    label="Org Code Parse Description"
                                    hint="LLM description guiding what to extract (e.g. 'The store number like org_1, org_2')"
                                >
                                    <Input
                                        value={config.settings.organizationCodeParseFieldNameDescription}
                                        onChange={setOrganizationCodeParseFieldNameDescription}
                                        placeholder="The store/company identifier from the document"
                                        size="sm"
                                    />
                                </FieldRow>
                            )}

                            <SectionHeader title="Vendor Lookup" />
                            <FieldRow label="Vendor Name Field" hint="Default: vendor_name">
                                <Input
                                    value={config.settings.taskConfig.vendorLookup.vendorNameField}
                                    onChange={(v) => setVendorLookupTask("vendorNameField", v)}
                                    placeholder="vendor_name"
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow label="Name Candidates Field" hint="Default: name_candidates">
                                <Input
                                    value={config.settings.taskConfig.vendorLookup.nameCandidatesField}
                                    onChange={(v) => setVendorLookupTask("nameCandidatesField", v)}
                                    placeholder="name_candidates"
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow label="Vendor ID Field" hint="Default: vendor_id">
                                <Input
                                    value={config.settings.taskConfig.vendorLookup.vendorIdField}
                                    onChange={(v) => setVendorLookupTask("vendorIdField", v)}
                                    placeholder="vendor_id"
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow label="Vendor Phone Field" hint="Default: vendor_phone">
                                <Input
                                    value={config.settings.taskConfig.vendorLookup.vendorPhoneField}
                                    onChange={(v) => setVendorLookupTask("vendorPhoneField", v)}
                                    placeholder="vendor_phone"
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow label="Min Composite Score" hint="Fuzzy match threshold (0–1). Empty = bridge default (0.85 SQL, 0.5 Sage 100).">
                                <Input
                                    value={config.settings.taskConfig.vendorLookup.minCompositeScore}
                                    onChange={(v) => setVendorLookupTask("minCompositeScore", v)}
                                    placeholder="0.85"
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow label="Search Check Name (GP-only)" hint="Also query PM00200.VNDCHKNM. Default: on.">
                                <Toggle
                                    isSelected={config.settings.taskConfig.vendorLookup.searchCheckName}
                                    onChange={(v) => setVendorLookupTaskBool("searchCheckName", v)}
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow label="Search Short Name (GP-only)" hint="Also query PM00200.VENDSHNM. Default: on.">
                                <Toggle
                                    isSelected={config.settings.taskConfig.vendorLookup.searchShortName}
                                    onChange={(v) => setVendorLookupTaskBool("searchShortName", v)}
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow
                                label="Vendor Aliases (JSON)"
                                hint='Manual {alias, canonical} pairs. Example: [{"alias":"ODFL","canonical":"OLD DOMINION FREIGHT LINE"}]'
                            >
                                <textarea
                                    className="block w-full rounded-md border border-secondary bg-primary px-3 py-2 font-mono text-xs text-primary placeholder:text-tertiary focus:ring-2 focus:ring-brand-500 focus:outline-none"
                                    rows={4}
                                    value={config.settings.taskConfig.vendorLookup.vendorAliasesJson}
                                    onChange={(e) => setVendorLookupTask("vendorAliasesJson", e.target.value)}
                                    placeholder='[{"alias":"ODFL","canonical":"OLD DOMINION FREIGHT LINE"}]'
                                />
                            </FieldRow>

                            <SectionHeader title="Receipt Lookup" />
                            <FieldRow label="Vendor ID Field" hint="Default: vendor_id">
                                <Input
                                    value={config.settings.taskConfig.receiptLookup.vendorIdField}
                                    onChange={(v) => setReceiptLookupTask("vendorIdField", v)}
                                    placeholder="vendor_id"
                                    size="sm"
                                />
                            </FieldRow>
                            <FieldRow label="Invoice Date Field" hint="Default: invoice_date">
                                <Input
                                    value={config.settings.taskConfig.receiptLookup.invoiceDateField}
                                    onChange={(v) => setReceiptLookupTask("invoiceDateField", v)}
                                    placeholder="invoice_date"
                                    size="sm"
                                />
                            </FieldRow>
                        </CollapsibleSection>
                    )}

                    {/* ---- Add Section ---- */}
                    {availableSections.length > 0 && (
                        <div className="flex items-center gap-2 pt-1 text-xs [&_p]:text-xs">
                            <Select
                                items={addSectionItems}
                                onSelectionChange={(key) => {
                                    if (key) addSection(key as OptionalSectionKey);
                                }}
                                placeholder="Add section..."
                                size="sm"
                                popoverClassName="[&_[slot=label]]:!text-xs [&_[slot=description]]:!text-xs"
                            >
                                {(item) => <Select.Item {...item} />}
                            </Select>
                        </div>
                    )}
                </div>
            )}

            {/* Save button */}
            <div className="flex items-center gap-3">
                <Button color="primary" size="sm" onClick={handleSave} isDisabled={isSaving}>
                    {isSaving ? "Saving..." : "Save Configuration"}
                </Button>
                {saveSuccess && <span className="text-sm text-success-primary">Saved.</span>}
                {saveError && <span className="text-sm text-error-primary">{saveError}</span>}
            </div>
        </div>
    );
}

// =============================================================================
// BRIDGE REGISTRATION CARD
// =============================================================================

function BridgeRegistrationCard({ registration }: { registration: BridgeRegistrationAdminListItemDto }) {
    const heartbeatTime = relativeTime(registration.lastHeartbeatAt);

    return (
        <div className="rounded-lg border border-secondary">
            {/* Card header */}
            <div className="flex items-center justify-between border-b border-secondary px-5 py-4">
                <div className="flex items-center gap-3">
                    <h4 className="text-sm font-semibold text-primary">{registration.name || registration.bridgeId}</h4>
                    <Badge color="brand" size="sm" type="pill-color">
                        {registration.environment}
                    </Badge>
                    <Badge color={statusBadgeColor(registration.status)} size="sm" type="pill-color">
                        {registration.status}
                    </Badge>
                </div>
                <div className="flex items-center gap-2 text-xs text-tertiary">
                    <span>
                        Last heartbeat: <span className={heartbeatTime.isStale ? "font-medium text-error-primary" : ""}>{heartbeatTime.text}</span>
                    </span>
                </div>
            </div>

            {/* Card body */}
            <div className="space-y-4 px-5 py-4">
                {/* Details grid */}
                <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                        <span className="text-tertiary">Bridge ID</span>
                        <div className="mt-0.5 font-mono text-xs text-primary">{registration.bridgeId}</div>
                    </div>
                    <div>
                        <span className="text-tertiary">Version</span>
                        <div className="mt-0.5 text-primary">{registration.version ?? "—"}</div>
                    </div>
                    <div>
                        <span className="text-tertiary">Created</span>
                        <div className="mt-0.5 text-primary">{new Date(registration.createdAt).toLocaleDateString()}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// ADD BRIDGE REGISTRATION MODAL
// =============================================================================

/** Environment options — must match EnvironmentEnum names on the backend. */
const ENVIRONMENT_OPTIONS = [
    { id: "Production", label: "Production" },
    { id: "Staging", label: "Staging" },
    { id: "Development", label: "Development" },
] as const;

function AddBridgeRegistrationModal({
    clientId,
    isOpen,
    onClose,
    onCreated,
}: {
    clientId: number;
    isOpen: boolean;
    onClose: () => void;
    onCreated: () => void;
}) {
    const adminApi = useAdminApi();
    const [bridgeId, setBridgeId] = useState("");
    const [name, setName] = useState("");
    const [environment, setEnvironment] = useState<string>("Production");
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Reset the form each time the modal is (re)opened.
    useEffect(() => {
        if (isOpen) {
            setBridgeId("");
            setName("");
            setEnvironment("Production");
            setError(null);
            setIsSaving(false);
        }
    }, [isOpen]);

    const canSubmit = bridgeId.trim() !== "" && name.trim() !== "" && !isSaving;

    const handleSubmit = useCallback(async () => {
        if (bridgeId.trim() === "" || name.trim() === "") {
            setError("Bridge ID and Name are required.");
            return;
        }
        setIsSaving(true);
        setError(null);
        try {
            await adminApi.createBridgeRegistration(clientId, {
                bridgeId: bridgeId.trim(),
                name: name.trim(),
                environment,
            });
            onCreated();
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to register bridge.");
        } finally {
            setIsSaving(false);
        }
    }, [adminApi, clientId, bridgeId, name, environment, onCreated, onClose]);

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && !isSaving && onClose()} isDismissable={!isSaving}>
            <Modal className="max-w-lg">
                <Dialog>
                    <div className="flex w-full flex-col gap-5 rounded-xl border border-secondary bg-primary p-6 shadow-xl">
                        <div>
                            <h2 className="text-lg font-semibold text-primary">Register Bridge</h2>
                            <p className="mt-1 text-sm text-tertiary">
                                Register a new on-prem bridge for this client. After creating it, use the Danger Zone below to generate the bridge secret, then
                                configure the bridge&apos;s <code className="rounded bg-quaternary px-1 py-0.5 font-mono text-xs">appsettings.json</code> with
                                the client code, secret, and backend URL.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-medium text-secondary">
                                    Bridge ID <span className="text-error-primary">*</span>
                                </label>
                                <p className="mt-0.5 mb-1 text-xs text-quaternary">Stable, globally-unique identifier (e.g. acme-prod-bridge).</p>
                                <Input value={bridgeId} onChange={setBridgeId} placeholder="acme-prod-bridge" size="sm" isInvalid={bridgeId.trim() === ""} />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-secondary">
                                    Name <span className="text-error-primary">*</span>
                                </label>
                                <p className="mt-0.5 mb-1 text-xs text-quaternary">Human-readable name shown in the bridge panel.</p>
                                <Input value={name} onChange={setName} placeholder="Production Bridge" size="sm" isInvalid={name.trim() === ""} />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-secondary">Environment</label>
                                <p className="mt-0.5 mb-1 text-xs text-quaternary">One registration is allowed per environment for this client.</p>
                                <Select
                                    items={[...ENVIRONMENT_OPTIONS]}
                                    selectedKey={environment}
                                    onSelectionChange={(key) => setEnvironment(String(key))}
                                    size="sm"
                                >
                                    {(item) => <Select.Item {...item} />}
                                </Select>
                            </div>
                        </div>

                        {error && <p className="text-sm text-error-primary">{error}</p>}

                        <div className="flex justify-end gap-3">
                            <Button color="secondary" size="md" onClick={onClose} isDisabled={isSaving}>
                                Cancel
                            </Button>
                            <Button color="primary" size="md" onClick={handleSubmit} isDisabled={!canSubmit}>
                                {isSaving ? "Registering..." : "Register Bridge"}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
}

// =============================================================================
// BRIDGE TAB (EXPORT)
// =============================================================================

export function BridgeTab({ clientId }: BridgeTabProps) {
    const adminApi = useAdminApi();

    const [registrations, setRegistrations] = useState<BridgeRegistrationAdminListItemDto[]>([]);
    const [bridgeConfig, setBridgeConfig] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Add bridge registration
    const [showAddModal, setShowAddModal] = useState(false);

    // Danger Zone — regenerate bridge secret
    const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [newSecret, setNewSecret] = useState<string | null>(null);
    const [secretCopied, setSecretCopied] = useState(false);
    const [secretSaved, setSecretSaved] = useState(false);
    const [regenerateError, setRegenerateError] = useState<string | null>(null);

    const fetchRegistrations = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await adminApi.listBridgeRegistrations(clientId);
            setRegistrations(res.data);
            setBridgeConfig(res.bridgeConfig ?? null);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load bridge registrations.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, clientId]);

    useEffect(() => {
        fetchRegistrations();
    }, [fetchRegistrations]);

    const handleRegenerateSecret = useCallback(async () => {
        setIsRegenerating(true);
        setRegenerateError(null);
        try {
            const res = await adminApi.regenerateBridgeSecret(clientId);
            setNewSecret(res.plaintextSecret);
            setSecretCopied(false);
            setSecretSaved(false);
            setShowRegenerateConfirm(false);
        } catch (err) {
            setRegenerateError(err instanceof Error ? err.message : "Failed to regenerate bridge secret.");
        } finally {
            setIsRegenerating(false);
        }
    }, [adminApi, clientId]);

    const handleCopySecret = useCallback(async () => {
        if (!newSecret) return;
        try {
            await navigator.clipboard.writeText(newSecret);
            setSecretCopied(true);
        } catch {
            // Fallback: select the text for manual copy
        }
    }, [newSecret]);

    const handleCloseSecretModal = useCallback(() => {
        setNewSecret(null);
        setSecretCopied(false);
        setSecretSaved(false);
    }, []);

    return (
        <div>
            {/* Header */}
            <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                    <h3 className="text-lg font-semibold text-primary">Bridge Registrations</h3>
                    <p className="text-sm text-tertiary">On-prem bridge instances registered for this client, their health status, and configuration.</p>
                </div>
                {/* A client uses a single bridge — only offer registration when none exists yet. */}
                {!isLoading && registrations.length === 0 && (
                    <Button color="primary" size="sm" iconLeading={Plus} onClick={() => setShowAddModal(true)}>
                        Register Bridge
                    </Button>
                )}
            </div>

            {/* Error */}
            {error && <p className="mb-3 text-sm text-error-primary">{error}</p>}

            {/* Loading */}
            {isLoading ? (
                <LoadingIndicator size="sm" label="Loading bridge registrations..." />
            ) : (
                <Fragment>
                    {/* Bridge registration cards */}
                    {registrations.length === 0 ? (
                        <p className="py-4 text-center text-sm text-quaternary">No bridge registrations found for this client.</p>
                    ) : (
                        <div className="mb-6 space-y-4">
                            {registrations.map((reg) => (
                                <BridgeRegistrationCard key={reg.id} registration={reg} />
                            ))}
                        </div>
                    )}

                    {/* Bridge configuration editor — always shown, independent of registrations */}
                    <BridgeConfigEditor configJson={bridgeConfig} clientId={clientId} onSaved={fetchRegistrations} />

                    {/* Danger Zone */}
                    <div className="border-error-primary mt-8 rounded-lg border p-5">
                        <h4 className="mb-4 text-base font-semibold text-error-primary">Danger Zone</h4>

                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <p className="text-sm font-medium text-primary">Bridge Secret</p>
                                <p className="text-sm text-tertiary">
                                    Regenerate the bridge authentication secret. The bridge will need to be reconfigured with the new secret.
                                </p>
                            </div>
                            <Button color="primary-destructive" size="sm" onClick={() => setShowRegenerateConfirm(true)}>
                                Regenerate Secret
                            </Button>
                        </div>
                    </div>
                </Fragment>
            )}

            {/* Confirmation dialog */}
            <ModalOverlay isOpen={showRegenerateConfirm} onOpenChange={(open) => !open && setShowRegenerateConfirm(false)} isDismissable={!isRegenerating}>
                <Modal className="max-w-md">
                    <Dialog>
                        <div className="flex w-full flex-col gap-5 rounded-xl border border-secondary bg-primary p-6 shadow-xl">
                            <div className="flex items-start gap-3">
                                <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-error-secondary">
                                    <AlertTriangle className="size-5 text-error-primary" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-primary">Regenerate Bridge Secret</h2>
                                    <p className="mt-1 text-sm text-tertiary">
                                        This will invalidate the current bridge secret. The bridge will lose authentication within 15 minutes (when its current
                                        JWT expires) and will not be able to acquire new tokens until reconfigured with the new secret.
                                    </p>
                                </div>
                            </div>

                            {regenerateError && <p className="text-sm text-error-primary">{regenerateError}</p>}

                            <div className="flex justify-end gap-3">
                                <Button color="secondary" size="md" onClick={() => setShowRegenerateConfirm(false)} isDisabled={isRegenerating}>
                                    Cancel
                                </Button>
                                <Button color="primary-destructive" size="md" onClick={handleRegenerateSecret} isDisabled={isRegenerating}>
                                    {isRegenerating ? "Regenerating..." : "Regenerate"}
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>

            {/* Secret display modal */}
            <ModalOverlay isOpen={newSecret !== null} onOpenChange={(open) => !open && secretSaved && handleCloseSecretModal()} isDismissable={false}>
                <Modal className="max-w-lg">
                    <Dialog>
                        <div className="flex w-full flex-col gap-5 rounded-xl border border-secondary bg-primary p-6 shadow-xl">
                            <div className="flex items-start gap-3">
                                <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-warning-secondary">
                                    <AlertTriangle className="size-5 text-warning-primary" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-primary">New Bridge Secret Generated</h2>
                                    <p className="mt-1 text-sm text-tertiary">
                                        This secret will only be shown once. Copy it now and configure it in the bridge&apos;s{" "}
                                        <code className="rounded bg-quaternary px-1 py-0.5 font-mono text-xs">appsettings.json</code> under{" "}
                                        <code className="rounded bg-quaternary px-1 py-0.5 font-mono text-xs">Bridge.ClientSecret</code>.
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 rounded-lg border border-secondary bg-secondary p-3">
                                <pre className="min-w-0 flex-1 overflow-x-auto font-mono text-sm text-primary">{newSecret}</pre>
                                <Button
                                    color={secretCopied ? "secondary" : "secondary"}
                                    size="sm"
                                    onClick={handleCopySecret}
                                    iconLeading={secretCopied ? CheckCircle : Copy01}
                                >
                                    {secretCopied ? "Copied" : "Copy"}
                                </Button>
                            </div>

                            <Checkbox label="I have saved this secret" isSelected={secretSaved} onChange={setSecretSaved} />

                            <div className="flex justify-end">
                                <Button color="primary" size="md" onClick={handleCloseSecretModal} isDisabled={!secretSaved}>
                                    Close
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>

            {/* Add bridge registration modal */}
            <AddBridgeRegistrationModal clientId={clientId} isOpen={showAddModal} onClose={() => setShowAddModal(false)} onCreated={fetchRegistrations} />
        </div>
    );
}
